import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UtilService } from '@service/util/util.service';
import { CarrierSearchQuery } from '@domain/interface/carrier-search-query';
import { CarrierSearchHelperService } from '@core/services/carrier-search-helper/carrier-search-helper.service';
import {
	C4ListResponse,
	CARRIER_LIST_TABLE,
	CarriersProjectsList,
	CarrierTable,
	CountMap,
	DATE_MDY,
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	RestService,
	TASK_STATUS_UUID,
	TASK_STATUS_UUID_MAP,
	TASK_TABLE,
	TaskDTO,
	TaskTable,
	User as ccUser,
	PROJECT_CARRIER_MAP_STATUS,
	INTEGRATION_TYPE,
} from 'cargo-chief-commons';
import { ACTIVE_USERS, ALL_ACCOUNT_USERS } from '@shared/utils/account-users';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	CARGOCHIEF_KEYS,
	COPY_TO_CLIPBOARD_MSGS,
	PLAN_PRODUCTS,
} from '@domain/constants/app-variables';
import { CarrierToProjectsMap } from '@domain/interface/carrier-to-projects-map';
import { QuickNotesViewDialog } from '@notes/quick-notes-view-dialog/quick-notes-view-dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmTextDialogComponent } from '@shared/confirm-text-dialog/confirm-text-dialog.component';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { Subject, firstValueFrom } from 'rxjs';
import { LaneDetailsComponent } from '../../../carrier-full-profile/lane-details/lane-details.component';
import { SharedService } from '@service/shared/shared.service';
import { takeUntil } from 'rxjs/operators';
import { UserCarrierMapHelperService } from '@service/userCarrierMapHelper/user-carrier-map-helper.service';
import { HighlightTextPipe } from '@shared/highlight-text.pipe';
import { IntegrationService } from '@service/integration/integration.service';
import { ProductsService } from '@service/products/products.service';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { NoteType } from '@notes/note-list/note-list.component';
import { IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { ProjectGroupService } from '@service/project-group/project-group.service';
import { MatLegacySlideToggle as MatSlideToggle } from '@angular/material/legacy-slide-toggle';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { SHOW_IN_NETWORK_LOGO, SHOW_ONBOARD_CARRIER_LINK } from '@shared/utils/in-network';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { HaulpayService } from '@service/haulpay/haulpay.service';
import { environment } from '@environment/environment';
import { getProjectsFilters } from '../../../v2/utils/projects-list';
import { buildRequest } from '../../../v2/procurement/procurement-list/build-projects-request';

@Component({
	selector: 'app-carrier-search-results',
	templateUrl: './carrier-search-results.component.html',
	styleUrls: ['./carrier-search-results.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [HighlightTextPipe],
})
export class CarrierSearchResultsComponent implements OnInit {
	searchObject: CarrierSearchQuery = null;
	isLoadingCarrierSearch: boolean = false;
	accountId: number = null;
	carriersList: Array<CarrierTable> = null;

	notesCountMap: CountMap = null;
	carriersProjectsList: Array<CarriersProjectsList> = null;
	allProjectsList: Array<ProjectDTO> = [];
	isLoadingProjectsList: boolean = false;
	projectsNextPage: boolean = false;
	PROJECTS_PAGE = 0;
	PROJECTS_PAGE_SIZE = 10;
	totalNumberOfProjects = null;
	COPY_TO_CLIPBOARD_MSGS = COPY_TO_CLIPBOARD_MSGS;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	recentlyViewedCarriers: Array<CarrierTable> = null;
	searchInput: Subject<string> = new Subject();
	allTasksList: Array<TaskTable> = null;
	userId: number = null;
	isLoadingTasks: boolean = false;
	isAdminOrOwner: boolean = false;

	@Input() carrierSearchString: string = '';
	@Input() laneDetails: LaneDetailsComponent;
	@Input() pendingHTTPRequests: Subject<any> = null;
	@Output() openCarrierFullProfile: EventEmitter<any> = new EventEmitter();
	@Output() closeCarrierFullProfile: EventEmitter<any> = new EventEmitter();

	IS_PROJECTS_ENABLED: boolean = false;
	IS_TASKS_ENABLED: boolean = false;
	DATE_MDY = DATE_MDY;
	SHOW_IN_NETWORK_LOGO = SHOW_IN_NETWORK_LOGO;
	SHOW_ONBOARD_CARRIER_LINK = SHOW_ONBOARD_CARRIER_LINK;
	CARRIER_QUALITY_ENABLED!: boolean;

	userCarrierMapList: Array<any> = null;
	userCarrierMap: any = {};
	usersList: Array<Owner> = null;
	allUsersList: Array<Owner> = null;
	isCarrierVettingIntegrationEnabled!: boolean;
	isIntegrationLinkAvailable: boolean = false;
	environment = environment;

	constructor(
		private urlService: UrlService,
		public cargochief: Cargochief,
		public utilService: UtilService,
		public carrierSearchHelper: CarrierSearchHelperService,
		public restService: RestService,
		private dialog: MatDialog,
		private segmentWrapper: SegmentWrapperService,
		public sharedService: SharedService,
		private userCarrierMapHelperService: UserCarrierMapHelperService,
		public integrationService: IntegrationService,
		public productsService: ProductsService,
		public carrierVettingService: CarrierVettingService,
		public haulpayService: HaulpayService,
		private accessCacheService: AccessCacheService,
		private projectGroupService: ProjectGroupService,
		public accountSettings: AccountSettingsService
	) {
		this.userId = this.cargochief.USER_ID;
	}

	ngOnInit() {
		this.IS_PROJECTS_ENABLED = this.cargochief.isProductEnabled(PLAN_PRODUCTS.PROJECT_CARRIERS);
		this.IS_TASKS_ENABLED = this.cargochief.isProductEnabled(PLAN_PRODUCTS.TASKS);
		this.searchObject = this.carrierSearchHelper.resetSearchObject();
		this.accountId = this.cargochief.ACCOUNT_ID;
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		if (this.productsService.ASSIGN_CARRIERS) {
			this.updateCarrierAssignee();
		}
		this.integrationService.getMyCarrierPacketsToken();
		this.isCarrierVettingIntegrationEnabled =
			this.integrationService.isCarrierVettingIntegrationEnabled();
		this.CARRIER_QUALITY_ENABLED = this.accountSettings.CARRIER_QUALITY;
		this.isIntegrationLinkAvailable = this.integrationService.isIntegrationUrlAvailable();
	}

	fetchRecentlyViewedCarriers() {
		if (this.utilService.isNullOrUndefined(this.recentlyViewedCarriers)) {
			this.getRecentlyViewedCarriers();
		}
		this.fireSegmentViewedRecentCarriers();
	}

	initiateSearch() {
		this.isLoadingCarrierSearch = false;
		if (IS_NOT_EMPTY(this.carrierSearchString)) {
			const isFullProfileOpened = this.cargochief.getItem(CARGOCHIEF_KEYS.IS_FULL_PROFILE_OPENED);
			if (!this.utilService.isNullOrUndefined(isFullProfileOpened) && isFullProfileOpened) {
				this.closeCarrierFullProfile.emit();
			}
			this.populateSearchObject();
		} else if (
			IS_EMPTY(this.carrierSearchString) &&
			!this.utilService.isNullOrUndefined(this.recentlyViewedCarriers) &&
			this.recentlyViewedCarriers.length > 0
		) {
			this.notesCountMap = null;
			this.carriersProjectsList = null;
			this.carriersList = null;
			this.copyRecentlyViewedCarrier();
		}
	}

	ngOnChanges() {
		if (!this.utilService.isNullOrUndefined(this.laneDetails)) {
			this.closeFullProfile();
		}
	}

	populateSearchObject() {
		const form = {
			searchText: this.carrierSearchString,
			inNetwork: false,
			powerUnits: null,
			hazmat: false,
			assignedToMe: false,
			hasTeams: false,
		};
		this.searchObject = Object.assign(
			{},
			this.carrierSearchHelper.populateSearchObject(form, [], null, [])
		);
		this.searchCarriers();
	}

	searchCarriers() {
		this.fireSegmentEventQuickSearched();
		this.carriersList = null;
		this.isLoadingCarrierSearch = true;
		const url = this.urlService.searchCarriers(0, 5, { withPrimaryContact: true });
		this.restService
			.POST(url, this.searchObject)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(result) => {
					this.carriersList = CARRIER_LIST_TABLE(result['content'], this.accountId);
					this.populateMetadata();
					this.isLoadingCarrierSearch = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to search carriers.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.carriersList = [];
					this.isLoadingCarrierSearch = false;
				}
			);
	}

	populateMetadata() {
		if (!this.utilService.isNullOrUndefined(this.carriersList) && this.carriersList.length > 0) {
			this.getCarrierNotesCount();
			this.getAllTasks();
			if (this.IS_PROJECTS_ENABLED) {
				this.getProjectsForCarriers();
				if (
					this.utilService.isNullOrUndefined(this.allProjectsList) ||
					this.allProjectsList.length == 0
				) {
					this.getAllProjects();
				}
			}
			if (this.productsService.ASSIGN_CARRIERS) {
				this.getAssigneeList();
				if (this.accessCacheService.getAccountUsers().length > 0) {
					this.populateUsersListForAccount(this.accessCacheService.getAccountUsers());
				} else {
					this.getUsersListForAccount();
				}
			}
		}
	}

	getCarrierNotesCount() {
		this.notesCountMap = null;
		const carrierIds = this.carriersList.map((carrier: CarrierTable) => carrier.id);
		const url = this.urlService.getCarrierNotesCount();
		const payload = {
			carrierIds: carrierIds,
		};
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					if (
						!this.utilService.isNullOrUndefined(response.data) &&
						!this.utilService.isNullOrUndefined(response.data.countMap)
					) {
						this.notesCountMap = response.data.countMap;
						this.updateNotesCount();
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get carriers notes count.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	getProjectsForCarriers() {
		this.carriersProjectsList = null;
		const carrierIds = this.carriersList.map((carrier: CarrierTable) => carrier.id);
		const payload = {
			carrierIds: carrierIds,
		};
		const url = this.urlService.getProjectsByCarrierIdsAndAccountId();
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					this.carriersProjectsList = response.data;
					this.buildProjectsDTO();
				},
				(error) => {
					console.error('Error while getting projects for carreirs', error);
				}
			);
	}

	getAllProjects() {
		this.isLoadingProjectsList = true;
		const filteredFilters = getProjectsFilters(
			[],
			this.accountSettings,
			this.utilService.isUserRoleAdmin(),
			this.cargochief.USER_ID
		);
		const requestDTO = buildRequest(
			this.cargochief.ACCOUNT_ID,
			this.accountSettings.ANNUAL_VOLUME,
			filteredFilters || [],
			this.PROJECTS_PAGE,
			this.PROJECTS_PAGE_SIZE,
			'created',
			'desc',
			[],
			this.cargochief.USER_ID,
			[],
			[]
		);
		const url = this.urlService.getProjectsList();
		this.restService
			.POST(url, requestDTO)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					this.allProjectsList = this.allProjectsList.concat(response.data);
					this.projectsNextPage = response.nextPage;
					if (this.projectsNextPage) {
						this.PROJECTS_PAGE += 1;
					}
					this.totalNumberOfProjects = response.totalElements;
					this.isLoadingProjectsList = false;
					this.buildProjectsDTO();
				},
				(error) => {
					console.error('Error while getting all projects', error);
					this.isLoadingProjectsList = false;
				}
			);
	}

	getAllTasks() {
		this.isLoadingTasks = true;
		this.allTasksList = null;
		const carrierIds = this.carriersList.map((carrier: CarrierTable) => carrier.id);
		const url = this.urlService.listTaskByIds();
		const payload = {
			carrierIDs: carrierIds,
			accountId: this.accountId,
			statusIds: [TASK_STATUS_UUID_MAP.TO_DO, TASK_STATUS_UUID_MAP.IN_PROGRESS],
			assigneeId: !this.isAdminOrOwner ? this.userId : undefined,
		};
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response: C4ListResponse<TaskDTO>) => {
					this.allTasksList = TASK_TABLE(response.data);
					this.isLoadingTasks = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get tasks list',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isLoadingTasks = false;
				}
			);
	}

	buildProjectsDTO() {
		if (
			!this.utilService.isNullOrUndefined(this.carriersProjectsList) &&
			!this.utilService.isNullOrUndefined(this.allProjectsList)
		) {
			setTimeout(() => {
				this.carriersList.map((carrier: CarrierTable) => {
					const pList: Array<CarriersProjectsList> = this.carriersProjectsList.filter(
						(cMap: CarriersProjectsList) => {
							return carrier.id == cMap.carrierId;
						}
					);
					if (
						!this.utilService.isNullOrUndefined(pList) &&
						pList.length > 0 &&
						!this.utilService.isNullOrUndefined(pList[0].projectDTOList)
					) {
						carrier.projects = pList[0].projectDTOList;
					} else {
						carrier.projects = [];
					}
				});
				this.carriersList.map((carrier: CarrierTable) => {
					carrier.carrierToProjectsMap = [];
					this.allProjectsList.map((project: ProjectDTO) => {
						let cProject: Array<ProjectDTO> = carrier.projects.filter((cp) => cp.id == project.id);
						let projectMap = {} as CarrierToProjectsMap;
						projectMap.projectId = project.id;
						projectMap.carrierId = carrier.id;
						projectMap.isCarrierMappedToProject =
							!this.utilService.isNullOrUndefined(cProject) && cProject.length > 0;
						projectMap.projectName = project.name;
						projectMap.type = project.type;
						carrier.carrierToProjectsMap.push(projectMap);
					});
				});
			}, 0);
		}
	}

	updateNotesCount() {
		if (
			!this.utilService.isNullOrUndefined(this.notesCountMap) &&
			!this.utilService.isNullOrUndefined(this.carriersList)
		) {
			setTimeout(() => {
				this.carriersList.map((carrier: CarrierTable) => {
					carrier.notesCount = this.notesCountMap[carrier.id];
				});
			}, 0);
		}
	}

	showNotes(carrier: CarrierTable) {
		const dialogRef = this.dialog.open(QuickNotesViewDialog, {
			width: '500px',
			data: {
				carrierId: carrier.id,
				truckLaneId: null,
				name: carrier.businessName,
				noteType: NoteType.CARRIER,
			},
		});
		dialogRef.componentInstance.notesDialogClosed.subscribe((data) => {
			this.carriersList.map((carrier: CarrierTable) => {
				if (carrier.id == data.carrierId) {
					carrier.notesCount = data.count;
				}
			});
			this.sharedService.sendCarrierSearchEvent({
				type: 'note',
				value: {
					notesListComponent: { totalNotes: data.count },
					capacitySearch: { carrierId: data.carrierId },
				},
			});
		});
	}

	async carrierProjectToggled(
		{
			project,
			isCarrierAddedToProject,
			slideToggleRef,
		}: {
			project: CarrierToProjectsMap;
			isCarrierAddedToProject: boolean;
			slideToggleRef: MatSlideToggle;
		},
		cIndex: number
	) {
		let url, requestMethod;
		let projectGroupPayload = {};
		let excludedAddReason: string = null;
		if (isCarrierAddedToProject) {
			// Soft warning: this carrier is excluded from receiving offers. Adding them to a workflow
			// lets them receive that workflow's loads. Require a reason (captured for audit) to proceed.
			if (this.carriersList?.[cIndex]?.excluded) {
				const carrierName = this.carriersList[cIndex].businessName;
				const reason = await firstValueFrom(
					this.dialog
						.open(ConfirmTextDialogComponent, {
							width: '600px',
							data: {
								title:
									`${carrierName} is excluded from receiving offers. Adding them to this workflow ` +
									`will let them receive this workflow's loads. Enter a reason to continue:`,
								placeholder: 'Reason',
								errorMessage: 'A reason is required to add an excluded carrier to a workflow.',
							},
						})
						.afterClosed()
				);
				const normalizedReason = reason?.trim();
				if (!normalizedReason) {
					slideToggleRef.checked = false;
					return;
				}
				excludedAddReason = normalizedReason;
			}
			if (IS_WORKFLOW_PROJECT_TYPE(project.type)) {
				let { projectGroupId, projectHasNoGroups } =
					await this.projectGroupService.selectProjectGroup(project.projectId);
				if (projectHasNoGroups) {
					projectGroupPayload = {
						manageProjectGroup: true,
					};
				} else if (IS_NOT_NULL_OR_UNDEFINED(projectGroupId)) {
					projectGroupPayload = {
						projectGroupId,
					};
				} else {
					this.utilService.showSnackbar(
						'Cannot add carrier to project without selecting a group',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					slideToggleRef.checked = !slideToggleRef.checked;
					return;
				}
			}
			this.fireCarrierAddedToProjectSegmentEvent(project.projectId, project.carrierId);
			url = this.urlService.addCarrierToProjects(project.carrierId);
			requestMethod = 'POST';
		} else {
			url = this.urlService.deleteProjectCarrierMapByProjectIdAndCarrierId(
				project.projectId,
				project.carrierId
			);
			requestMethod = 'DELETE';
		}
		const payload = {
			projectIds: [project.projectId],
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			...projectGroupPayload,
			...(excludedAddReason ? { reason: excludedAddReason } : {}),
		};
		this.restService[requestMethod](url, payload).subscribe(
			(result) => {
				this.utilService.showSnackbar(
					`Carrier ${isCarrierAddedToProject ? 'added to' : 'removed from'} project.`,
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.sharedService.sendCarrierSearchEvent({
					type: 'project',
					value: { carrierId: project.carrierId, projectId: project.projectId },
				});
				if (isCarrierAddedToProject) {
					const projectCarrierMap = this.allProjectsList.filter((projectDTO) => {
						return project.projectId == projectDTO.id;
					});
					if (
						!this.utilService.isNullOrUndefined(projectCarrierMap) &&
						projectCarrierMap.length > 0
					) {
						if (this.utilService.isNullOrUndefined(this.carriersList[cIndex].projects)) {
							this.carriersList[cIndex].projects = [];
						}
						this.carriersList[cIndex].projects.push(projectCarrierMap[0]);
					}
				} else {
					if (!this.utilService.isNullOrUndefined(this.carriersList[cIndex].projects)) {
						this.carriersList[cIndex].projects = this.carriersList[cIndex].projects.filter(
							(projectDTO) => {
								return project.projectId != projectDTO.id;
							}
						);
					}
				}
				project.isCarrierMappedToProject = isCarrierAddedToProject;
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to update carrier to projects now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				project.isCarrierMappedToProject = !isCarrierAddedToProject;
			}
		);
	}

	fireCarrierAddedToProjectSegmentEvent(projectId: string, carrierId: string) {
		const payload = {
			carrier: carrierId,
			projectId: projectId,
			page: 'Carriers',
		};
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_added_carrier_to_projects,
			payload
		);
	}

	getRecentlyViewedCarriers() {
		this.isLoadingCarrierSearch = true;
		const url = this.urlService.recentlyViewedCarriers();
		this.restService
			.GET(url)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(result) => {
					this.recentlyViewedCarriers = CARRIER_LIST_TABLE(result['data'], this.accountId);
					this.copyRecentlyViewedCarrier();
					this.isLoadingCarrierSearch = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Cannot fetch recently viewed carriers.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isLoadingCarrierSearch = false;
				}
			);
	}

	searchInputChanged(event) {
		this.searchInput.next(event);
	}

	copyRecentlyViewedCarrier() {
		this.carriersList = null;
		if (IS_NOT_NULL_OR_UNDEFINED(this.recentlyViewedCarriers)) {
			this.carriersList = [...this.recentlyViewedCarriers];
			this.populateMetadata();
		}
	}

	showFullProfile(carrier: CarrierTable) {
		this.openCarrierFullProfile.emit(carrier);
	}

	closeFullProfile() {
		if (
			!this.utilService.isNullOrUndefined(this.laneDetails) &&
			!this.utilService.isNullOrUndefined(this.laneDetails.capacitySearch) &&
			!this.utilService.isNullOrUndefined(this.laneDetails.capacitySearch.carrierId)
		) {
			const notesListComponent = this.laneDetails.notesListComponent;
			const notesCount = !this.utilService.isNullOrUndefined(notesListComponent)
				? notesListComponent.noteCount
				: 0;
			const carrierId = !this.utilService.isNullOrUndefined(this.laneDetails.capacitySearch)
				? this.laneDetails.capacitySearch.carrierId
				: null;
			this.notesCountMap = {
				...this.notesCountMap,
				[carrierId]: notesCount,
			};
			this.closeCarrierFullProfile.emit();
		}
	}

	reloadProjectsList() {
		if (this.IS_PROJECTS_ENABLED) {
			this.carriersProjectsList = null;
			this.allProjectsList = [];
			this.PROJECTS_PAGE = 0;
			this.getProjectsForCarriers();
			this.getAllProjects();
		}
	}

	getTasksForCarrier(carrierId: string): Array<TaskTable> {
		if (!this.utilService.isNullOrUndefined(this.allTasksList)) {
			return this.allTasksList.filter((task) => task.carrierId == carrierId);
		}
	}

	tasksUpdated(updatedTasks: Array<TaskTable>) {
		const doneStatus = this.getDoneStatusString();
		this.allTasksList = this.allTasksList
			.map((task) => {
				const filteredTask = updatedTasks.find((updatedTask) => updatedTask.id == task.id);
				if (filteredTask) {
					return filteredTask;
				}
				return task;
			})
			.filter((task) => task.status !== doneStatus);
	}

	taskAdded(newTask: TaskTable) {
		this.allTasksList = [newTask, ...this.allTasksList];
	}

	getDoneStatusString(): string {
		return TASK_STATUS_UUID[TASK_STATUS_UUID_MAP.DONE];
	}

	getAssigneeList() {
		let userCarrierMapList: any = this.cargochief.getItem(CARGOCHIEF_KEYS.USER_CARRIER_MAPS);
		if (IS_NOT_EMPTY(userCarrierMapList)) {
			this.userCarrierMapList = JSON.parse(userCarrierMapList);
		} else {
			this.userCarrierMapList = [];
		}
		this.buildUserCarrierMap();
	}

	getUsersListForAccount() {
		this.usersList = null;
		const url = this.urlService.getActiveUsersByAccountIdUrl(this.cargochief.ACCOUNT_ID, 0, 100);
		this.restService.GET(url).subscribe(
			(response) => {
				this.usersList = [];
				this.populateUsersListForAccount(response.data);
				this.buildUserCarrierMap();
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to get all active users.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	populateUsersListForAccount(users: ccUser[]) {
		if (IS_NULL_OR_UNDEFINED(this.usersList)) {
			this.usersList = [];
		}
		this.usersList = ACTIVE_USERS(users);
		// Labels come from the account cache: the REST fallback is active-scoped and paged,
		// so it cannot supply names for non-ACTIVE assignees.
		const cachedUsers = this.accessCacheService.getAccountUsers();
		this.allUsersList = ALL_ACCOUNT_USERS(cachedUsers?.length ? cachedUsers : users);
	}

	buildUserCarrierMap() {
		if (
			!this.utilService.isNullOrUndefined(this.userCarrierMapList) &&
			this.userCarrierMapList.length > 0 &&
			!this.utilService.isNullOrUndefined(this.allUsersList) &&
			this.allUsersList.length > 0
		) {
			this.userCarrierMapList.map((cMap: any) => {
				// Names come from the full list: an existing assignee may no longer be ACTIVE.
				const assignee: Owner = this.allUsersList.filter(
					(user) => user.userId == cMap.assigneeId
				)[0];
				if (!this.utilService.isNullOrUndefined(assignee)) {
					this.userCarrierMap[cMap.carrierId] = assignee.name;
				}
			});
		}
	}

	addUserCarrierMap(user: Owner, carrierId: string) {
		const payload = this.userCarrierMapHelperService.createPayload(user.userId, carrierId, false);
		const url = this.urlService.userCarrierMap();
		this.restService.POST(url, payload).subscribe(
			(result) => {
				this.userCarrierMap[carrierId] = user.name;
				this.utilService.showSnackbar('User Assigned.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.sharedService.sendCarrierAssignedEvent({
					deleted: false,
					carrierId: carrierId,
					assigneeName: user.name,
					userCarrierMap: result.data,
				});
				this.userCarrierMapList = this.userCarrierMapList.filter(
					(cMap: any) => cMap.carrierId != carrierId
				);
				this.userCarrierMapList.push(result.data);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to set Assignee. Please try again.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	deleteUserCarrierMap(carrierId: string, userCarrierMapId: string) {
		const payload = {
			userCarrierMapIDs: [userCarrierMapId],
		};
		const url = this.urlService.userCarrierMap();
		this.restService.DELETE(url, payload).subscribe(
			(result) => {
				delete this.userCarrierMap[carrierId];
				this.utilService.showSnackbar('Assignee removed.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.sharedService.sendCarrierAssignedEvent({
					deleted: true,
					carrierId: carrierId,
				});
				this.userCarrierMapList = this.userCarrierMapList.filter(
					(cMap: any) => cMap.carrierId != carrierId
				);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to delete Assignee. Please try again.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	setAssignee(user: Owner, carrierId: string) {
		if (!this.utilService.isNullOrUndefined(user)) {
			const cMap: any = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0];
			if (
				this.utilService.isNullOrUndefined(cMap) ||
				(!this.utilService.isNullOrUndefined(cMap) && cMap.assigneeId != user.userId)
			) {
				this.addUserCarrierMap(user, carrierId);
			}
		} else if (
			this.utilService.isNullOrUndefined(user) &&
			!this.utilService.isNullOrUndefined(this.userCarrierMap)
		) {
			const userCarrierMapId: string = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0].id;
			if (!this.utilService.isNullOrUndefined(userCarrierMapId)) {
				this.deleteUserCarrierMap(carrierId, userCarrierMapId);
			}
		}
	}

	getUserIdAssigned(carrierId: string): number {
		return this.userCarrierMapList.filter((cMap: any) => cMap.carrierId == carrierId)[0].assigneeId;
	}

	updateCarrierAssignee() {
		this.sharedService.getCarrierAssignedEvent().subscribe((data: any) => {
			if (!this.utilService.isNullOrUndefined(data)) {
				this.userCarrierMap = Object.assign(
					{},
					this.sharedService.updateUserCCarrierMap(data, this.userCarrierMap)
				);
			}
		});
	}

	fireSegmentViewedRecentCarriers() {
		const eventProperties = {
			user: this.utilService.getUserDataForSegmentEvent(),
		};
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_viewed_recent_carriers,
			eventProperties
		);
	}

	fireSegmentEventQuickSearched() {
		const eventProperties = {
			user: this.utilService.getUserDataForSegmentEvent(),
			carrierSearch: this.searchObject,
		};
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_quick_searched,
			eventProperties
		);
	}

	onboardCarrier(carrier: any) {
		let integrationType = this.integrationService.getEnabledCarrierVettingIntegration();
		this.showMyCarrierEmailConfirmation(carrier, integrationType);
	}

	showMyCarrierEmailConfirmation(carrier: any, integrationType: INTEGRATION_TYPE) {
		let payload: any = this.utilService.getUserDataForSegmentEvent();
		payload.search = carrier;
		payload.page = 'user_quick_searched';
		this.integrationService.showMyCarrierEmailConfirmation(
			carrier.businessName,
			carrier.dot,
			carrier.id,
			payload,
			null,
			null,
			integrationType
		);
	}
}
